import React, { createContext, useMemo, useReducer } from 'react';

import { User } from '../models';
import { AUTH_LS_USER_KEY } from '../constants';

const userData = window.localStorage.getItem(AUTH_LS_USER_KEY);
const user: User | null = userData ? new User(userData) : null;

interface AuthState {
  isAuth: boolean
  user: User | null
}

const initialState: AuthState = {
  isAuth: !!user,
  user,
};

export enum AuthActionType {
  USER_LOGIN = 'USER_LOGIN',
  USER_LOGOUT = 'USER_LOGOUT',
  USER_UPDATE = 'USER_UPDATE',
  USER_SYNC = 'USER_SYNC',
  USER_CONFIRM = 'USER_CONFIRM',
}

interface AuthAction {
  type: AuthActionType
}

interface UserLoginAction extends AuthAction {
  payload: {
    user: User
  }
}

interface UserConfirmAction extends AuthAction {
  payload: {
    user: User
  }
}

interface UserLogoutAction extends AuthAction { }

interface UserUpdateAction extends AuthAction {
  payload: {
    user: User
  }
}

interface UserSyncAction extends AuthAction {
  payload: {
    user: User
  }
}

export type AuthActions = UserLoginAction | UserLogoutAction | UserUpdateAction | UserSyncAction;

const authReducer = (state: AuthState, action: AuthActions): AuthState => {
  const { type } = action;
  switch (type) {
    case AuthActionType.USER_LOGIN: {
      const { payload } = action as UserLoginAction;
      window.localStorage.setItem(AUTH_LS_USER_KEY, JSON.stringify(payload.user));
      return {
        ...state,
        user: payload.user,
        isAuth: true,
      };
    }
    case AuthActionType.USER_CONFIRM: {
      const { payload } = action as UserConfirmAction;
      window.localStorage.setItem(AUTH_LS_USER_KEY, JSON.stringify(payload.user));
      return {
        ...state,
        user: payload.user,
        isAuth: true,
      };
    }
    case AuthActionType.USER_LOGOUT:
      window.localStorage.removeItem(AUTH_LS_USER_KEY);
      return {
        ...state,
        user: null,
        isAuth: false,
      };
    case AuthActionType.USER_UPDATE: {
      const { payload } = action as UserUpdateAction;
      window.localStorage.setItem(AUTH_LS_USER_KEY, JSON.stringify(payload.user));
      return {
        ...state,
        user: payload.user,
      };
    }
    case AuthActionType.USER_SYNC: {
      const { payload } = action as UserSyncAction;
      window.localStorage.setItem(AUTH_LS_USER_KEY, JSON.stringify(payload.user));
      return {
        ...state,
        user: payload.user,
      };
    }
    default:
      return state;
  }
};

export const AuthContext = createContext({} as { authState: AuthState, authDispatch: React.Dispatch<AuthActions> });

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const providerValue = useMemo(() => ({
    authState: state,
    authDispatch: dispatch,
  }), [state]);
  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};
